import React, { ReactNode, useState } from 'react';
import {
  Popover as MuiPopover,
  PopoverProps as MuiPopoverProps,
  Box,
  ClickAwayListener,
  useTheme,
  styled,
  alpha,
} from '@mui/material';
import { shadows } from '@mui/system';

export interface PopoverProps {
  /**
   * The anchor element for the popover
   */
  anchorEl?: HTMLElement | null;
  /**
   * Content to be shown inside the popover
   */
  children: ReactNode;
  /**
   * Whether the popover is open
   */
  open: boolean;
  /**
   * Function to handle the close event
   */
  onClose: () => void;
  /**
   * Position of the popover relative to the anchor element
   */
  placement?: 'top' | 'right' | 'bottom' | 'left' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'right-start' | 'right-end' | 'left-start' | 'left-end';
  /**
   * Extra classes to be applied to the popover container
   */
  className?: string;
  /**
   * Style object for the popover container
   */
  style?: React.CSSProperties;
  /**
   * Style object for the popover content
   */
  contentStyle?: React.CSSProperties;
  /**
   * Add an arrow to the popover
   */
  arrow?: boolean;
  /**
   * Animation duration in milliseconds
   */
  animationDuration?: number;
  /**
   * Width of the popover
   */
  width?: number | string;
  /**
   * Maximum height of the popover (scrolls if content exceeds)
   */
  maxHeight?: number | string;
  /**
   * Elevation/shadow depth (0-24)
   */
  elevation?: number;
}

// Maps our placement props to MUI's anchorOrigin and transformOrigin
const getPositionProps = (
  placement: PopoverProps['placement']
): Pick<MuiPopoverProps, 'anchorOrigin' | 'transformOrigin'> => {
  switch (placement) {
    case 'top':
      return {
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        transformOrigin: { vertical: 'bottom', horizontal: 'center' },
      };
    case 'top-start':
      return {
        anchorOrigin: { vertical: 'top', horizontal: 'left' },
        transformOrigin: { vertical: 'bottom', horizontal: 'left' },
      };
    case 'top-end':
      return {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        transformOrigin: { vertical: 'bottom', horizontal: 'right' },
      };
    case 'bottom':
      return {
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        transformOrigin: { vertical: 'top', horizontal: 'center' },
      };
    case 'bottom-start':
      return {
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
      };
    case 'bottom-end':
      return {
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        transformOrigin: { vertical: 'top', horizontal: 'right' },
      };
    case 'right':
      return {
        anchorOrigin: { vertical: 'center', horizontal: 'right' },
        transformOrigin: { vertical: 'center', horizontal: 'left' },
      };
    case 'right-start':
      return {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
      };
    case 'right-end':
      return {
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        transformOrigin: { vertical: 'bottom', horizontal: 'left' },
      };
    case 'left':
      return {
        anchorOrigin: { vertical: 'center', horizontal: 'left' },
        transformOrigin: { vertical: 'center', horizontal: 'right' },
      };
    case 'left-start':
      return {
        anchorOrigin: { vertical: 'top', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'right' },
      };
    case 'left-end':
      return {
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'bottom', horizontal: 'right' },
      };
    default:
      return {
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        transformOrigin: { vertical: 'top', horizontal: 'center' },
      };
  }
};

// Styled content container
const ContentContainer = styled(Box)(({ theme }) => ({

  borderRadius: theme.shape.borderRadius,
  maxWidth: '100%',
  overflowY: 'auto',
  boxShadow: 'none',
}));

// Arrow component for the popover
const Arrow = styled('div')<{ placement?: PopoverProps['placement'] }>(
  ({ theme, placement }) => {
    const getArrowStyles = () => {
      if (!placement) return {};
      
      // Base arrow styles
      const baseStyles = {
        position: 'absolute',
        width: 10,
        height: 10,
        backgroundColor: theme.palette.background.paper,
        shadows:'none',
        transform: 'rotate(45deg)',
        zIndex: 0,
      };

      // Position-specific styles
      if (placement.startsWith('top')) {
        return { ...baseStyles, bottom: -5, left: '50%', marginLeft: -5 };
      } else if (placement.startsWith('bottom')) {
        return { ...baseStyles, top: -5, left: '50%', marginLeft: -5 };
      } else if (placement.startsWith('left')) {
        return { ...baseStyles, right: -5, top: '50%', marginTop: -5 };
      } else if (placement.startsWith('right')) {
        return { ...baseStyles, left: -5, top: '50%', marginTop: -5 };
      }

      return baseStyles;
    };

    return getArrowStyles();
  }
);

/**
 * A reusable Popover component based on Material UI
 */
const Popover = ({
  anchorEl,
  children,
  open,
  onClose,
  placement = 'bottom',
  className,
  style,
  contentStyle,
  arrow = false,
  animationDuration = 250,
  width = 'auto',
  maxHeight = 300,
  elevation = 8,
}: PopoverProps) => {
  const theme = useTheme();
  const positionProps = getPositionProps(placement);

  return (
    <MuiPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      {...positionProps}
      className={className}
      style={style}
      PaperProps={{
        elevation,
        sx: {
          borderRadius: "10px",
          width: width,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'none',
          overflow: 'visible',
          ...style,
        },
      }}
      TransitionProps={{
        timeout: animationDuration,
      }}
      disableRestoreFocus
    >
      <ClickAwayListener onClickAway={onClose}>
        <Box sx={{ position: 'relative' }}>
          {arrow && <Arrow placement={placement} />}
          <ContentContainer
            sx={{
              maxHeight,
              ...contentStyle,
            }}
          >
            {children}
          </ContentContainer>
        </Box>
      </ClickAwayListener>
    </MuiPopover>
  );
};

export default Popover;
